/**
 * Alert Component - Constants
 * =============================
 * Tipos y mapas de estilo del alert (Figma: node 2483:1983). El padre
 * (`ecAlert`) aporta la caja + ícono + dismiss Y el layout del contenido;
 * `title`/`description` cubren el uso simple, y los slots nombrados
 * `ecAlertTitle`/`ecAlertText`/`ecAlertActions` (marcadores sin componente
 * propio, igual que `<ec-label>` en los form controls — ver
 * `transclude` en component.ts) permiten reemplazar cada parte con markup
 * libre, p. ej. `<ec-button>` dentro de `<ec-alert-actions>`. El padre sigue
 * siendo dueño del layout (gap entre texto y acciones) sea cual sea el
 * origen del contenido.
 *
 * Simplificación respecto a Figma: los botones de acción NO son bindings
 * (`showButtons`/`mainButtonText`/etc, como en el archivo de diseño) — el
 * sistema ya tiene `ecButton`; codificar layout de botones en props del
 * alert sería la "mega-binding" que el patrón compound busca evitar.
 */

import { getIcon } from '../../icons';

// ============================================================================
// TYPES
// ============================================================================

export type AlertTheme = 'light' | 'dark';
export type AlertIconType = 'none' | 'circle-alert' | 'triangular-alert' | 'circular-info' | 'check';

export interface AlertThemeStyles {
  container: string;
  icon: string;
  title: string;
  text: string;
  dismiss: string;
}

export interface AlertDefaultValues {
  theme: AlertTheme;
  icon: AlertIconType;
}

// ============================================================================
// THEME (Figma property1: "Default" -> light / "dark" -> dark)
// ============================================================================

export const THEME_CLASSES: Record<AlertTheme, AlertThemeStyles> = {
  light: {
    container: 'ec-bg-color-surface ec-border ec-border-color-border',
    icon: 'ec-text-color-primary',
    title: 'ec-text-color-text',
    text: 'ec-text-color-text',
    dismiss: 'ec-text-color-text'
  },
  dark: {
    // Sin token existente para este navy (Figma primary/900) — agregado en
    // colors.css/themes/tailwind.config.js siguiendo la escala color-primary
    // ya existente (-light=200, DEFAULT=600, -dark=700).
    container: 'ec-bg-color-primary-900',
    icon: 'ec-text-white',
    title: 'ec-text-white',
    // #F0F3FF (Figma primary/50) coincide exactamente con el token existente
    // color-accent-subtle (mismo hex, pensado para fondos de forms) — se
    // reutiliza acá como color de texto en vez de sumar un token duplicado.
    text: 'ec-text-color-accent-subtle',
    dismiss: 'ec-text-white'
  }
};

// ============================================================================
// BASE
// ============================================================================

export const BASE_CLASSES = {
  container: 'ec-flex ec-flex-col ec-w-full ec-p-4 ec-rounded-2xl ec-gap-3 ec-font-main',
  row: 'ec-flex ec-gap-3 ec-items-start ec-w-full',
  icon: 'ec-shrink-0 ec-w-6 ec-h-6',
  // gap-4 (16px): separación entre el bloque de texto (título+descripción) y las acciones.
  content: 'ec-flex ec-flex-col ec-flex-1 ec-min-w-0 ec-gap-4',
  textBlock: 'ec-flex ec-flex-col ec-gap-3 ec-w-full',
  title: 'ec-text-base ec-font-semibold ec-tracking-[-0.48px] ec-w-full',
  text: 'ec-text-sm ec-tracking-[-0.42px] ec-w-full',
  actions: 'ec-flex ec-items-center ec-gap-3 ec-w-full',
  dismiss:
    'ec-shrink-0 ec-w-4 ec-h-4 ec-bg-transparent ec-border-0 ec-p-0 ec-cursor-pointer ' +
    'hover:ec-opacity-70 focus-visible:ec-outline-none focus-visible:ec-ring-1 focus-visible:ec-ring-current'
};

// ============================================================================
// ICONS
// ============================================================================

/** Ícono principal (registro central, variante solid — coincide con "Alert Icon" de Figma). */
export const ICONS: Record<AlertIconType, string> = {
  none: '',
  'circle-alert': getIcon('circle-alert', 'solid') || '',
  'triangular-alert': getIcon('triangular-alert', 'solid') || '',
  'circular-info': getIcon('circular-info', 'solid') || '',
  check: getIcon('check') || ''
};

/** Ícono del botón de cierre — fijo, no configurable. */
export const DISMISS_ICON: string = getIcon('dismiss', 'solid') || '';

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULTS: AlertDefaultValues = {
  theme: 'light',
  icon: 'circle-alert'
};
